<template>
    <DModal
        :title="$t('certificate_domains.manage_sans_modal_title', { domain: certificate.domain ?? '' })"
        :show="showModal"
        @close="cancel"
    >
        <div
            v-if="showModal"
            class="san-edit-body"
        >
            <p class="text-muted">
                {{ $t('certificate_domains.manage_sans_description') }}
            </p>

            <div
                v-if="confirming"
                class="confirmation-section"
            >
                <p class="meta-label">
                    {{ $t('certificate_domains.pending_changes_label') }}
                </p>
                <ul class="diff-list">
                    <li
                        v-for="domain in pendingRemoves"
                        :key="`remove-${domain}`"
                        class="diff-remove"
                    >
                        <span class="badge">{{ $t('certificate_domains.badge_remove') }}</span>
                        <span class="mono">{{ domain }}</span>
                    </li>
                    <li
                        v-for="domain in pendingAdds"
                        :key="`add-${domain}`"
                        class="diff-add"
                    >
                        <span class="badge">{{ $t('certificate_domains.badge_add') }}</span>
                        <span class="mono">{{ domain }}</span>
                    </li>
                    <li
                        v-if="pendingPrimary !== null"
                        class="diff-primary"
                    >
                        <span class="badge">{{ $t('certificate_domains.badge_primary') }}</span>
                        <span class="mono">{{ pendingPrimary }}</span>
                    </li>
                </ul>
                <p class="warning">
                    {{ $t('certificate_domains.reissue_warning_prefix') }} <strong>{{ $t('certificate_domains.reissue_warning_strong') }}</strong>
                    {{ $t('certificate_domains.reissue_warning_suffix') }}
                </p>
            </div>

            <template v-else>
                <div class="san-edit-section">
                    <p class="meta-label">
                        {{ $t('certificate_domains.current_san_list_label') }}
                    </p>
                    <ul class="san-list">
                        <li
                            v-for="domain in activeDomains"
                            :key="domain"
                            class="san-row"
                            :class="{ 'is-primary': domain === certificate.domain, 'is-removed': removeSet[domain] }"
                        >
                            <span class="mono san-domain">{{ domain }}</span>
                            <span class="san-action">
                                <DButton
                                    v-if="canSetPrimary && !isPrimary(domain) && !removeSet[domain]"
                                    icon="star"
                                    color="link"
                                    mini
                                    :title="$t('certificate_domains.set_primary_tooltip')"
                                    @click="setPrimary(domain)"
                                />
                                <span
                                    v-if="isPrimary(domain)"
                                    class="badge primary-badge"
                                >{{ $t('certificate_domains.badge_primary') }}</span>
                                <DButton
                                    v-else-if="canRemove && !removeSet[domain]"
                                    icon="close"
                                    color="link"
                                    mini
                                    :title="$t('certificate_domains.remove_san_tooltip')"
                                    @click="markForRemoval(domain)"
                                />
                                <DButton
                                    v-else-if="removeSet[domain]"
                                    icon="undo"
                                    color="link"
                                    mini
                                    :title="$t('certificate_domains.undo_removal_tooltip')"
                                    @click="undoRemoval(domain)"
                                />
                            </span>
                        </li>
                    </ul>
                </div>

                <div
                    v-if="canAdd"
                    class="san-edit-section"
                >
                    <p class="meta-label">
                        {{ $t('certificate_domains.add_new_sans_label') }}
                    </p>
                    <DomainListInput
                        v-model="addList"
                    />
                </div>
            </template>
        </div>

        <template #buttons>
            <DButton
                v-if="confirming"
                icon="arrow-left"
                :label="$t('certificate_domains.back_button')"
                color="secondary"
                @click="confirming = false"
            />
            <DButton
                v-if="confirming"
                icon="check"
                :label="$t('certificate_domains.confirm_reissue_button')"
                color="primary"
                @click="submit"
            />
            <DButton
                v-else
                icon="arrow-right"
                :label="$t('certificate_domains.review_changes_button')"
                color="primary"
                :disabled="!hasPendingChanges"
                @click="confirming = true"
            />
        </template>
    </DModal>
</template>

<script>
import { router } from '@inertiajs/vue3'
import DomainListInput from '../DomainListInput.vue'

export default {
    name: 'SanMutationModal',

    components: { DomainListInput },

    props: {
        certificate: {
            type: Object,
            default: () => ({})
        },
        showModal: {
            type: Boolean,
            default: false
        },
        canAdd: {
            type: Boolean,
            default: false
        },
        canRemove: {
            type: Boolean,
            default: false
        },
        canSetPrimary: {
            type: Boolean,
            default: false
        }
    },

    emits: ['close-san-mutation-modal'],

    data () {
        return {
            confirming: false,
            addList: [],
            removeSet: {},
            primaryOverride: null
        }
    },

    computed: {
        activeDomains () {
            const rows = Array.isArray(this.certificate?.domains) ? this.certificate.domains : []
            const active = rows
                .filter(row => row.active !== false)
                .map(row => row.domain)

            // Always include the primary first, even if no domain rows exist
            // for legacy single-domain certs.
            const primary = this.certificate?.domain
            if (primary && !active.includes(primary)) {
                return [primary, ...active]
            }
            return active
        },

        pendingAdds () {
            return this.addList
                .map(entry => (typeof entry === 'string' ? entry.trim() : ''))
                .filter(entry => entry.length > 0)
        },

        pendingRemoves () {
            return Object.keys(this.removeSet).filter(key => this.removeSet[key])
        },

        pendingPrimary () {
            if (this.primaryOverride === null || this.primaryOverride === this.certificate?.domain) {
                return null
            }
            return this.primaryOverride
        },

        hasPendingChanges () {
            return this.pendingAdds.length > 0 || this.pendingRemoves.length > 0 || this.pendingPrimary !== null
        }
    },

    watch: {
        showModal (open) {
            if (!open) {
                this.reset()
            }
        }
    },

    methods: {
        cancel () {
            this.$emit('close-san-mutation-modal')
        },

        reset () {
            this.confirming = false
            this.addList = []
            this.removeSet = {}
            this.primaryOverride = null
        },

        isPrimary (domain) {
            if (this.pendingPrimary !== null) {
                return domain === this.pendingPrimary
            }
            return domain === this.certificate?.domain
        },

        markForRemoval (domain) {
            this.removeSet = { ...this.removeSet, [domain]: true }
        },

        undoRemoval (domain) {
            const next = { ...this.removeSet }
            delete next[domain]
            this.removeSet = next
        },

        setPrimary (domain) {
            // Promoting a SAN you previously marked for removal would
            // contradict itself — flip the removal off automatically.
            if (this.removeSet[domain]) {
                this.undoRemoval(domain)
            }
            this.primaryOverride = domain
        },

        submit () {
            const payload = {
                add: this.pendingAdds,
                remove: this.pendingRemoves
            }

            if (this.pendingPrimary !== null) {
                payload.primary = this.pendingPrimary
            }

            router.post(
                this.$route('certificates.domains.manage', { certificate: this.certificate.id }),
                payload,
                {
                    onSuccess: () => this.cancel(),
                    onError: errors => {
                        // Surface validation failures so the operator
                        // can see what's wrong. Inertia drops these
                        // into `$page.props.errors` by default, but
                        // the modal doesn't render them inline.
                        // eslint-disable-next-line no-console
                        console.warn('Manage SANs submit failed:', errors)
                    },
                    preserveScroll: true
                }
            )
        }
    }
}
</script>

<style scoped>
.san-edit-body {
    padding: 0.5rem 1rem 1.5rem;
}
.san-edit-section {
    margin-top: 1rem;
}
.meta-label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary-color, #6c757d);
    margin-bottom: 0.4rem;
}
.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
    font-size: 0.875rem;
}
.san-list {
    list-style: none;
    padding: 0;
    margin: 0;
    border: 1px solid var(--bs-border-color, #dee2e6);
    border-radius: 4px;
}
.san-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.6rem;
}
.san-row + .san-row {
    border-top: 1px solid var(--bs-border-color, #dee2e6);
}
.san-row.is-removed .san-domain {
    text-decoration: line-through;
    color: var(--bs-secondary-color, #6c757d);
}
.san-row.is-primary .san-domain {
    font-weight: 600;
}
.san-domain {
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-all;
}
.san-action {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}
.badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: var(--bs-secondary-bg-subtle, #e9ecef);
    color: var(--bs-secondary-color, #6c757d);
}
.confirmation-section {
    margin-top: 0.5rem;
}
.diff-list {
    list-style: none;
    padding: 0;
    margin: 0 0 0.75rem;
}
.diff-list li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0;
}
.diff-add .badge {
    background: var(--bs-success-bg-subtle, #d1e7dd);
    color: var(--bs-success-text-emphasis, #0a3622);
}
.diff-remove .badge {
    background: var(--bs-danger-bg-subtle, #f8d7da);
    color: var(--bs-danger-text-emphasis, #58151c);
}
.diff-primary .badge {
    background: var(--bs-primary-bg-subtle, #cfe2ff);
    color: var(--bs-primary-text-emphasis, #052c65);
}
.primary-badge {
    margin-left: 0.25rem;
}
.warning {
    background: var(--bs-warning-bg-subtle, #fff3cd);
    border: 1px solid var(--bs-warning-border-subtle, #ffe69c);
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-top: 0.5rem;
}
</style>
